import { useGetters, useStore } from 'vuex-composition-helpers'
import { computed, Ref, unref } from 'vue'
import type { MultiselectOption } from '@ironsight/types'
import User from '@/models/user'
import { useUsersQuery } from '@/api/users'

export function useAllUsersFilter(): { options: Ref<MultiselectOption[]> } {
  const { getVisibleUsers } = useGetters('users', ['getVisibleUsers'])
  const options = computed<MultiselectOption[]>(() =>
    getVisibleUsers.value
      .filter((u): u is User & { id: number; name: string } => u.id != null && u.name != null)
      .map((u) => ({
        label: u.name,
        value: u.id,
      })),
  )

  return { options }
}

export function useRequesterFilter(): { options: Ref<MultiselectOption[]> } {
  const { getVisibleUsers } = useGetters('users', ['getVisibleUsers'])
  const options = computed<MultiselectOption[]>(() =>
    getVisibleUsers.value
      .filter((u) => ['Dispatcher', 'Superadmin', 'Customer'].some((r) => u.roles.includes(r)))
      .filter((u): u is User & { id: number; name: string } => u.id != null && u.name != null)
      .map((u) => ({
        label: u.name,
        value: u.id,
      })),
  )

  return { options }
}

export function useSubmitterFilter(): { options: Ref<MultiselectOption[]> } {
  return useRequesterFilter()
}

export function useFieldWorkerFilter(): {
  options: Ref<MultiselectOption[]>
} {
  const { getVisibleUsers } = useGetters('users', ['getVisibleUsers'])
  const options = computed<MultiselectOption[]>(() =>
    getVisibleUsers.value
      .filter((u) => u.roles.includes('Operator'))
      .filter((u): u is User & { id: number; name: string } => u.id != null && u.name != null)
      .map((u) => ({
        label: u.name,
        value: u.id,
      })),
  )

  return { options }
}

export function useUsersFromOrgFilter() {
  const { data: users } = useUsersQuery()

  const store = useStore()
  const userOrgId = store.getters['permissions/getOrganizationId']

  const options = computed<MultiselectOption[]>(() => {
    return (
      users.value
        ?.filter((u) => u.organizationId === userOrgId)
        .map((u) => ({
          label: u.name,
          value: u.id,
        })) ?? []
    )
  })

  return { options }
}

export function useUsersWithUuidFromOrgFilter() {
  const { data: users } = useUsersQuery()

  const store = useStore()
  const userOrgId = store.getters['permissions/getOrganizationId']

  const options = computed<MultiselectOption[]>(() => {
    return (
      users.value
        ?.filter((u) => u.organizationId === userOrgId)
        .map((u) => ({
          label: u.name,
          value: u.uuid,
        })) ?? []
    )
  })

  return { options }
}
